// ═══════════════════════════════════════════════════════════════
// CapacityView — Top-level portfolio triage
// Path: src/views/CapacityView.jsx
//
// Phase 2 of 4 of the CapacityView blueprint.
// Answers: "Where should I spend my time this week?"
//
// Three-zone layout per the Zone Framework:
//   Zone 1: header (title, help, back/home)
//   Zone 2: filter + sort bar (inline beneath the toolbar)
//   Content: left filter rail | center ranked list | right summary
//   Zone 3: brand footer
//
// Data: consumes allCapacity, allGifts, lastContactByMember, and
// capacityWeights from AppContext. No extra fetches on mount —
// everything loaded at login.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import {
  Target,
  Filter,
  ChevronDown,
  TrendingUp,
  User,
  Clock,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../constants/appConfig";
import { HELP } from "../constants/helpContent";
import { ViewLayout } from "../components/ui/ViewLayout";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { Card } from "../components/ui/Card";
import { InfoBubble } from "../components/ui/InfoBubble";
import {
  CAPACITY_BANDS,
  getCapacityBand,
  getBandMidpoint,
  computeFiveYearGiving,
  computeOpportunityGap,
  computeCapacityConfidence,
  resolveEffectiveBand,
} from "../utils/capacityScoring";

// ── Helpers ──────────────────────────────────────────────────────

const fmtMoney = (n) => {
  if (n == null) return "—";
  if (n === 0) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
};

const fmtMoneyFull = (n) => {
  if (n == null) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

const fmtDaysSince = (dateStr) => {
  if (!dateStr) return "Never";
  const then = new Date(dateStr);
  const days = Math.floor((Date.now() - then.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
};

const scoreColor = (score) => {
  if (score >= 75) return "#10b981";
  if (score >= 50) return "#f59e0b";
  if (score >= 25) return "#ef4444";
  return "#94a3b8";
};

const confidenceBucket = (score) => {
  if (score >= 75) return "high";
  if (score >= 40) return "medium";
  return "low";
};

// ── Sub-components ───────────────────────────────────────────────

const FilterRail = ({
  palette,
  portfolioFilter,
  setPortfolioFilter,
  directors,
  myDirectorName,
  bandFilter,
  setBandFilter,
  confidenceFilter,
  setConfidenceFilter,
  pathwayFilter,
  setPathwayFilter,
  pathways,
  resultCount,
  onReset,
}) => {
  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: palette.text3,
    letterSpacing: 0.4,
    marginBottom: 6,
    marginTop: 14,
  };

  const selectStyle = {
    width: "100%",
    padding: "7px 10px",
    borderRadius: 7,
    fontSize: FONT_SIZES.sm,
    fontFamily: FONT_FAMILY,
    background: palette.inputBg,
    color: palette.inputColor,
    border: `1px solid ${palette.inputBorder}`,
    outline: "none",
    boxSizing: "border-box",
  };

  return (
    <Card style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <Filter size={ICON_SIZES.sm} color={palette.accent1} />
        <div
          style={{
            fontSize: FONT_SIZES.sm,
            fontWeight: 700,
            color: palette.text1,
          }}
        >
          Filters
        </div>
        <span
          style={{
            marginLeft: "auto",
            fontSize: 11,
            color: palette.text3,
            fontFamily: FONT_FAMILY,
          }}
        >
          {resultCount} match{resultCount === 1 ? "" : "es"}
        </span>
      </div>

      <label style={labelStyle}>
        PORTFOLIO
        <InfoBubble
          size={ICON_SIZES.xs}
          text="Narrow the list to your own assigned donors ('Mine'), another director's portfolio, or the full organization. Directors are read from the Directors settings."
        />
      </label>
      <select
        style={selectStyle}
        value={portfolioFilter}
        onChange={(e) => setPortfolioFilter(e.target.value)}
      >
        <option value="all">All donors</option>
        {myDirectorName && (
          <option value="mine">Mine ({myDirectorName})</option>
        )}
        <option disabled>─── other directors ───</option>
        {directors.map((d) => (
          <option key={d.id} value={`director:${d.full_name}`}>
            {d.full_name}
          </option>
        ))}
      </select>

      <label style={labelStyle}>
        CAPACITY BAND
        <InfoBubble
          size={ICON_SIZES.xs}
          text="Filter to a specific band. 'Not yet estimated' finds donors whose band is system-suggested (not director-confirmed) — a good research queue."
        />
      </label>
      <select
        style={selectStyle}
        value={bandFilter}
        onChange={(e) => setBandFilter(e.target.value)}
      >
        <option value="all">All bands</option>
        <option value="unrated">Not yet estimated</option>
        {CAPACITY_BANDS.filter((b) => b.id !== "unknown").map((b) => (
          <option key={b.id} value={b.id}>{b.label}</option>
        ))}
      </select>

      <label style={labelStyle}>
        CONFIDENCE
        <InfoBubble
          size={ICON_SIZES.xs}
          text="Filter by how defensible each donor's estimate is. 'Low' surfaces donors needing research or an assessment; 'High' is your most defensible portfolio."
        />
      </label>
      <select
        style={selectStyle}
        value={confidenceFilter}
        onChange={(e) => setConfidenceFilter(e.target.value)}
      >
        <option value="all">All levels</option>
        <option value="high">High (75%+)</option>
        <option value="medium">Medium (40–74%)</option>
        <option value="low">Low (below 40%)</option>
      </select>

      <label style={labelStyle}>PATHWAY STAGE</label>
      <select
        style={selectStyle}
        value={pathwayFilter}
        onChange={(e) => setPathwayFilter(e.target.value)}
      >
        <option value="all">All stages</option>
        {pathways.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>

      <button
        onClick={onReset}
        style={{
          marginTop: 18,
          width: "100%",
          padding: "7px 10px",
          borderRadius: 7,
          background: "transparent",
          border: `1px solid ${palette.cardBorder}`,
          color: palette.text2,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: FONT_FAMILY,
        }}
      >
        Reset filters
      </button>
    </Card>
  );
};

const DonorRow = ({ row, palette, onClick, pathways }) => {
  const band = getCapacityBand(row.effectiveBand);
  const isSuggested = row.bandSource === "suggested";
  const isNone = row.bandSource === "none";
  const cScore = row.confidence.score;
  const pathway = pathways.find((p) => p.id === row.member.pathway);
  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 2fr) 120px 110px 110px 110px minmax(0, 1.2fr)",
        gap: 14,
        alignItems: "center",
        padding: "12px 14px",
        borderBottom: `1px solid ${palette.cardBorder}`,
        cursor: "pointer",
        transition: "background 0.12s",
        fontSize: FONT_SIZES.sm,
        fontFamily: FONT_FAMILY,
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = `${palette.accent1}08`)
      }
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 700,
            color: palette.text1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.member.firstName} {row.member.lastName}
        </div>
        <div
          style={{
            fontSize: 11,
            color: palette.text3,
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginTop: 2,
          }}
        >
          {pathway && <span>{pathway.name}</span>}
          {row.member.assignedDirector && (
            <span>· {row.member.assignedDirector}</span>
          )}
        </div>
      </div>
      <div>
        <div
          style={{
            color: isSuggested ? palette.text2 : palette.text1,
            fontWeight: 600,
            fontStyle: isSuggested ? "italic" : "normal",
          }}
        >
          {isNone ? "—" : band ? band.label : "—"}
        </div>
        {isSuggested && (
          <div
            style={{
              fontSize: 10,
              color: palette.accent1,
              fontWeight: 700,
              letterSpacing: 0.3,
              marginTop: 1,
            }}
          >
            SUGGESTED
          </div>
        )}
        {isNone && (
          <div style={{ fontSize: 10, color: palette.text3 }}>
            No signal
          </div>
        )}
      </div>
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: scoreColor(cScore),
              flexShrink: 0,
            }}
          />
          <span style={{ fontWeight: 700, color: palette.text1 }}>
            {cScore}%
          </span>
        </div>
      </div>
      <div
        style={{
          fontWeight: 700,
          color: row.opportunityGap > 0 ? "#f59e0b" : palette.text3,
        }}
      >
        {row.opportunityGap == null ? "—" : fmtMoney(row.opportunityGap)}
      </div>
      <div style={{ color: palette.text2 }}>
        {fmtDaysSince(row.lastTouched)}
      </div>
      <div
        style={{
          fontSize: 11,
          color: palette.text2,
          fontStyle: "italic",
          lineHeight: 1.4,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {row.nextActionHint || "—"}
      </div>
    </div>
  );
};

const SummaryRail = ({ palette, rows, maxedOutOnly, onToggleMaxed }) => {
  const stats = useMemo(() => {
    let totalCapacity = 0;
    let totalFiveYear = 0;
    let totalGap = 0;
    let confidenceSum = 0;
    let confidenceN = 0;
    let countMaxed = 0;
    let countUnrated = 0;
    rows.forEach((r) => {
      const mp = getBandMidpoint(r.effectiveBand);
      if (mp != null) totalCapacity += mp;
      if (r.bandSource !== "assigned") countUnrated += 1;
      totalFiveYear += r.fiveYearGiving;
      if (r.opportunityGap != null) {
        totalGap += r.opportunityGap;
        if (r.opportunityGap === 0 && mp != null) countMaxed += 1;
      }
      if (r.confidence?.score != null) {
        confidenceSum += r.confidence.score;
        confidenceN += 1;
      }
    });
    return {
      totalCapacity,
      totalFiveYear,
      totalGap,
      avgConfidence: confidenceN ? Math.round(confidenceSum / confidenceN) : 0,
      countMaxed,
      countUnrated,
      total: rows.length,
    };
  }, [rows]);

  const metricRow = (label, value, hint, accent, helpText, onClick, active) => (
    <div
      onClick={onClick}
      style={{
        padding: "10px 8px",
        margin: "0 -8px",
        borderRadius: 6,
        borderBottom: `1px solid ${palette.cardBorder}`,
        cursor: onClick ? "pointer" : "default",
        background: active ? `${palette.accent1}14` : "transparent",
        transition: "background 0.15s",
      }}
      onMouseEnter={
        onClick
          ? (e) =>
              (e.currentTarget.style.background = active
                ? `${palette.accent1}20`
                : `${palette.accent1}0a`)
          : undefined
      }
      onMouseLeave={
        onClick
          ? (e) =>
              (e.currentTarget.style.background = active
                ? `${palette.accent1}14`
                : "transparent")
          : undefined
      }
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: active ? palette.accent1 : palette.text3,
          letterSpacing: 0.4,
          marginBottom: 3,
          display: "flex",
          alignItems: "center",
        }}
      >
        {label}
        {helpText && <InfoBubble size={ICON_SIZES.xs} text={helpText} />}
        {active && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 9,
              fontWeight: 800,
              color: palette.accent1,
              letterSpacing: 0.5,
            }}
          >
            FILTERED
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: accent || palette.text1,
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          style={{
            fontSize: 11,
            color: palette.text3,
            marginTop: 2,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );

  return (
    <Card style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 6,
        }}
      >
        <TrendingUp size={ICON_SIZES.sm} color={palette.accent1} />
        <div
          style={{
            fontSize: FONT_SIZES.sm,
            fontWeight: 700,
            color: palette.text1,
          }}
        >
          Portfolio Summary
        </div>
      </div>
      {metricRow(
        "TOTAL RATED CAPACITY",
        fmtMoneyFull(stats.totalCapacity),
        `${stats.total - stats.countUnrated} confirmed · ${stats.countUnrated} suggested`,
        null,
        "Sum of every donor's capacity band midpoint in the current view. 'Confirmed' = a director assigned the band; 'suggested' = system-inferred from giving history."
      )}
      {metricRow(
        "5-YEAR GIVING",
        fmtMoneyFull(stats.totalFiveYear),
        "What they've actually given",
        null,
        "Total of all gifts received from donors in the view during the last five years — the base against which the opportunity gap is measured."
      )}
      {metricRow(
        "OPPORTUNITY GAP",
        fmtMoneyFull(stats.totalGap),
        "Room to grow toward rated capacity",
        "#f59e0b",
        "Total rated capacity minus 5-year giving. This is the dollar amount of unrealized potential you could theoretically capture from donors in this view over the next 5 years."
      )}
      {metricRow(
        "AVG CONFIDENCE",
        `${stats.avgConfidence}%`,
        `${stats.countUnrated} donors still awaiting director sign-off`,
        null,
        "Average confidence score across the filtered view. A low average means your portfolio rests on soft ground — prioritize assessment work on donors whose rows are italicized 'SUGGESTED'."
      )}
      {metricRow(
        "AT OR ABOVE RATING",
        String(stats.countMaxed),
        maxedOutOnly
          ? "Click to show all donors again"
          : stats.countMaxed > 0
          ? "Click to isolate these donors"
          : "Consider raising the capacity band",
        null,
        "Donors whose 5-year giving has met or exceeded their rated capacity midpoint. Either you under-rated them or their capacity has grown — open each and review the band. Click this tile to filter the list to just these donors.",
        stats.countMaxed > 0 || maxedOutOnly ? onToggleMaxed : null,
        maxedOutOnly
      )}
    </Card>
  );
};

// ── Main view ────────────────────────────────────────────────────

export const CapacityView = () => {
  const {
    palette,
    members,
    allCapacity,
    allWealthProfiles,
    allGifts,
    lastContactByMember,
    capacityWeights,
    pathways,
    directors,
    userProfile,
    navigate,
    goBack,
    goHome,
  } = useApp();

  const chapterHasWealthData =
    Object.keys(allWealthProfiles || {}).length > 0;

  const myDirectorName = userProfile?.full_name || "";
  const [portfolioFilter, setPortfolioFilter] = useState("all");
  const [bandFilter, setBandFilter] = useState("all");
  const [confidenceFilter, setConfidenceFilter] = useState("all");
  const [pathwayFilter, setPathwayFilter] = useState("all");
  const [maxedOutOnly, setMaxedOutOnly] = useState(false);
  const [sortBy, setSortBy] = useState("gap");

  const resetFilters = () => {
    setPortfolioFilter("all");
    setBandFilter("all");
    setConfidenceFilter("all");
    setPathwayFilter("all");
    setMaxedOutOnly(false);
    setSortBy("gap");
  };

  // Index gifts by member for efficient per-row computation
  const giftsByMember = useMemo(() => {
    const map = {};
    (allGifts || []).forEach((g) => {
      const mid = g.member_id || g.memberId;
      if (!mid) return;
      if (!map[mid]) map[mid] = [];
      map[mid].push(g);
    });
    return map;
  }, [allGifts]);

  const rows = useMemo(() => {
    return (members || []).map((member) => {
      const memberGifts = (giftsByMember[member.id] || []).sort((a, b) =>
        (b.gift_date || "").localeCompare(a.gift_date || "")
      );
      const capacity = allCapacity[member.id] || null;
      const fiveYearGiving = computeFiveYearGiving(memberGifts);
      const effective = resolveEffectiveBand(capacity, fiveYearGiving);
      const opportunityGap = computeOpportunityGap(
        effective.bandId,
        fiveYearGiving
      );
      const wealthProfile = allWealthProfiles?.[member.id] || null;
      const confidence = computeCapacityConfidence({
        member,
        capacity,
        contactLogs: [],
        gifts: memberGifts,
        engagementScore: member.engagementScore || null,
        wealthProfile,
        weights: capacityWeights,
        chapterHasWealthData,
      });
      const lastContact = lastContactByMember[member.id] || null;
      const lastGift = memberGifts[0]?.gift_date || null;
      const lastTouched =
        lastContact && lastGift
          ? lastContact > lastGift
            ? lastContact
            : lastGift
          : lastContact || lastGift;
      const nextActionHint =
        confidence.gaps[0]?.missing ||
        (opportunityGap > 0
          ? "Gap to rated capacity — make the ask when timing is right."
          : null);
      return {
        member,
        capacity,
        effectiveBand: effective.bandId,
        bandSource: effective.source,
        confidence,
        fiveYearGiving,
        opportunityGap,
        lastTouched,
        nextActionHint,
      };
    });
  }, [
    members,
    giftsByMember,
    allCapacity,
    allWealthProfiles,
    chapterHasWealthData,
    lastContactByMember,
    capacityWeights,
  ]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      // Portfolio
      if (portfolioFilter === "mine") {
        if (r.member.assignedDirector !== myDirectorName) return false;
      } else if (portfolioFilter.startsWith("director:")) {
        const name = portfolioFilter.slice("director:".length);
        if (r.member.assignedDirector !== name) return false;
      }
      // Band — filter against the effective (assigned OR suggested) band,
      // so freshly-installed chapters with no director assessments still
      // get meaningful results.
      if (bandFilter === "unrated") {
        // "Not yet confirmed by a director" — matches rows where the band
        // is system-suggested (or none at all), regardless of value
        if (r.bandSource === "assigned") return false;
      } else if (bandFilter !== "all") {
        if (r.effectiveBand !== bandFilter) return false;
      }
      // Confidence
      if (confidenceFilter !== "all") {
        if (confidenceBucket(r.confidence.score) !== confidenceFilter)
          return false;
      }
      // Pathway
      if (pathwayFilter !== "all") {
        if (r.member.pathway !== pathwayFilter) return false;
      }
      // "At or above rating" — donor is giving at or past their band midpoint
      if (maxedOutOnly) {
        const mp = getBandMidpoint(r.effectiveBand);
        if (mp == null) return false;
        if (r.fiveYearGiving < mp) return false;
      }
      return true;
    });
  }, [
    rows,
    portfolioFilter,
    bandFilter,
    confidenceFilter,
    pathwayFilter,
    maxedOutOnly,
    myDirectorName,
  ]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    switch (sortBy) {
      case "capacity":
        copy.sort(
          (a, b) =>
            (getBandMidpoint(b.effectiveBand) || 0) -
            (getBandMidpoint(a.effectiveBand) || 0)
        );
        break;
      case "confidence":
        copy.sort((a, b) => a.confidence.score - b.confidence.score);
        break;
      case "contact":
        copy.sort((a, b) => {
          const ad = a.lastTouched || "0";
          const bd = b.lastTouched || "0";
          return ad.localeCompare(bd);
        });
        break;
      case "gap":
      default:
        copy.sort(
          (a, b) => (b.opportunityGap || 0) - (a.opportunityGap || 0)
        );
    }
    return copy;
  }, [filtered, sortBy]);

  const handleRowClick = (member) => {
    navigate("memberDetail", member, { from: "capacity" });
  };

  const toolbarButtonStyle = (active) => ({
    padding: "7px 12px",
    borderRadius: 7,
    border: `1px solid ${active ? palette.accent1 : palette.cardBorder}`,
    background: active ? `${palette.accent1}14` : "transparent",
    color: active ? palette.accent1 : palette.text2,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT_FAMILY,
  });

  return (
    <ViewLayout>
      <ZoneOneHeader
        title="Capacity"
        subtitle={`${sorted.length} of ${members.length} donors`}
        icon={Target}
        helpContent={HELP.capacity}
        onBack={goBack}
        onHome={goHome}
      />
      <ZoneTwoToolbar />

      {/* Zone 2: sort bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 24px",
          gap: 12,
          flexWrap: "wrap",
          background: palette.toolbarBg,
          borderBottom: `1px solid ${palette.cardBorder}`,
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: palette.text3,
              letterSpacing: 0.3,
            }}
          >
            SORT BY
          </span>
          <button
            style={toolbarButtonStyle(sortBy === "gap")}
            onClick={() => setSortBy("gap")}
          >
            Opportunity Gap
          </button>
          <button
            style={toolbarButtonStyle(sortBy === "capacity")}
            onClick={() => setSortBy("capacity")}
          >
            Capacity
          </button>
          <button
            style={toolbarButtonStyle(sortBy === "confidence")}
            onClick={() => setSortBy("confidence")}
          >
            Confidence (lowest first)
          </button>
          <button
            style={toolbarButtonStyle(sortBy === "contact")}
            onClick={() => setSortBy("contact")}
          >
            Last Contact (oldest first)
          </button>
        </div>
      </div>

      {/* Content: 3-panel grid */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 20px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(200px, 240px) 1fr minmax(220px, 280px)",
            gap: 16,
            alignItems: "start",
          }}
        >
          <FilterRail
            palette={palette}
            portfolioFilter={portfolioFilter}
            setPortfolioFilter={setPortfolioFilter}
            directors={directors || []}
            myDirectorName={myDirectorName}
            bandFilter={bandFilter}
            setBandFilter={setBandFilter}
            confidenceFilter={confidenceFilter}
            setConfidenceFilter={setConfidenceFilter}
            pathwayFilter={pathwayFilter}
            setPathwayFilter={setPathwayFilter}
            pathways={pathways || []}
            resultCount={sorted.length}
            onReset={resetFilters}
          />

          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(0, 2fr) 120px 110px 110px 110px minmax(0, 1.2fr)",
                gap: 14,
                padding: "10px 14px",
                fontSize: 11,
                fontWeight: 700,
                color: palette.text3,
                letterSpacing: 0.4,
                borderBottom: `1px solid ${palette.cardBorder}`,
                background: `${palette.accent1}04`,
              }}
            >
              <div>DONOR</div>
              <div>
                CAPACITY
                <InfoBubble
                  size={ICON_SIZES.xs}
                  text="The 5-year giving capacity band this donor could reach if maximally inclined. 'SUGGESTED' means the band was inferred from their giving history; once you confirm it in the donor's Capacity tab the italics go away."
                />
              </div>
              <div>
                CONFIDENCE
                <InfoBubble
                  size={ICON_SIZES.xs}
                  text="How defensible the capacity estimate is, 0–100%. Six weighted components: giving history, relationship depth, your assessment, external wealth data, engagement, and network/role. Tune the weights in Settings → Scoring."
                />
              </div>
              <div>
                5YR GAP
                <InfoBubble
                  size={ICON_SIZES.xs}
                  text="Capacity band midpoint minus what the donor has given in the last 5 years. High gap = big upside. Zero gap = consider raising the band."
                />
              </div>
              <div>
                LAST TOUCH
                <InfoBubble
                  size={ICON_SIZES.xs}
                  text="The most recent gift or logged contact, whichever is newer. Sort by 'Last Contact (oldest first)' to find donors you've been neglecting regardless of their capacity."
                />
              </div>
              <div>
                NEXT MOVE
                <InfoBubble
                  size={ICON_SIZES.xs}
                  text="The single highest-leverage action to strengthen this donor's confidence score. Pulled from the confidence breakdown's gap analysis — the lowest-scoring component with a specific fix."
                />
              </div>
            </div>
            {sorted.length === 0 ? (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: palette.text3,
                  fontSize: FONT_SIZES.sm,
                }}
              >
                No donors match these filters.
              </div>
            ) : (
              sorted.map((row) => (
                <DonorRow
                  key={row.member.id}
                  row={row}
                  palette={palette}
                  pathways={pathways || []}
                  onClick={() => handleRowClick(row.member)}
                />
              ))
            )}
          </Card>

          <SummaryRail
            palette={palette}
            rows={filtered}
            maxedOutOnly={maxedOutOnly}
            onToggleMaxed={() => setMaxedOutOnly((v) => !v)}
          />
        </div>
      </div>

      <ZoneThreeFooter />
    </ViewLayout>
  );
};
